import { useCallback, useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../api/client";
import { m5Api } from "../../api/m5";
import { PageHeader } from "../../components/ComingSoon";
import { Button, ErrorText, TextInput } from "../../components/ui";
import { LoadState, useLoad } from "../admin/shared";

export default function MyPaymentMethod() {
  const [nonce, setNonce] = useState(0);
  const loader = useCallback(() => m5Api.myMethod(), [nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad(loader);
  const [type, setType] = useState<"PHONE" | "EMAIL" | "">("");
  const [vid, setVid] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reset = () => { setType(""); setVid(null); setCode(""); setVerified(false); setName(""); setAgree(false); setErr(null); setNonce((n) => n + 1); };
  const run = async (fn: () => Promise<void>) => { setErr(null); try { await fn(); } catch (e) { setErr(errorMessage(e)); } };
  return (
    <div data-testid="my-payment-method-page" className="animate-rise space-y-6">
      <PageHeader title="Payment method" />
      <LoadState error={error} loading={!data && !error} testId="my-method-load" />
      {data && (
        <>
          <section className="rounded-sm border border-white/10 bg-surface p-5" data-testid="my-method-current">
            <p className="label-caps text-zinc-500">Current</p>
            <p className="mt-2 text-lg text-white" data-testid="my-method-label">{data.current.default ? "Check (default – no electronic election)" : data.current.label}</p>
            {data.on_hold && <p className="mt-2 text-xs text-amber-200" data-testid="my-method-hold">Your new payment contact is being confirmed by an administrator. Payments wait until it's confirmed.</p>}
            {data.current.method === "ZELLE" && (
              <div className="mt-3 flex gap-2">
                <Button variant="secondary" onClick={() => run(async () => { await m5Api.electCheck(true); toast.success("Zelle revoked — you'll be paid by check"); reset(); })} data-testid="my-method-revoke-zelle">Revoke Zelle (pay me by check)</Button>
              </div>
            )}
            {data.current.method !== "CHECK" && data.current.method !== "ZELLE" && <Button variant="secondary" className="mt-3" onClick={() => run(async () => { await m5Api.electCheck(false); reset(); })} data-testid="my-method-choose-check">Switch to check</Button>}
          </section>
          <section className="space-y-4 rounded-sm border border-white/10 p-5" data-testid="my-method-zelle-flow">
            <p className="label-caps text-signal">Choose Zelle (optional)</p>
            <p className="text-xs text-zinc-400" data-testid="my-method-nevada-help">{data.nevada_help}</p>
            <div className="flex flex-wrap gap-2">
              {data.contacts.map((c) => (
                <button key={c.type} onClick={() => run(async () => { setType(c.type); setVerified(false); setCode(""); const r = await m5Api.contactCode(c.type); setVid(r.verification_id); toast.success(`Code sent to ${r.recipient_masked}`); })}
                  className={`rounded-sm border px-3 py-2 text-sm ${type === c.type ? "border-signal text-white" : "border-white/15 text-zinc-300"}`} data-testid={`my-method-contact-${c.type.toLowerCase()}`}>
                  {c.type === "PHONE" ? "Verified phone" : "Email"} {c.masked}
                </button>
              ))}
            </div>
            {vid && !verified && (
              <div className="flex gap-2">
                <TextInput className="w-40" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="my-method-code-input" />
                <Button onClick={() => run(async () => { await m5Api.contactCheck(vid, code); setVerified(true); })} disabled={code.length !== 6} data-testid="my-method-code-check">Confirm code</Button>
              </div>
            )}
            {verified && (
              <div className="space-y-3" data-testid="my-method-consent">
                <p className="text-xs font-semibold text-amber-300">DRAFT – requires legal review · {data.consent_text_version}</p>
                <p className="rounded-sm bg-ink p-3 text-sm text-zinc-200" data-testid="my-method-consent-text">{data.consent_text}</p>
                <TextInput placeholder="Type your full name" value={name} onChange={(e) => setName(e.target.value)} data-testid="my-method-signature" />
                <label className="flex items-center gap-2 text-sm text-zinc-300"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="my-method-agree" />I elect, in writing, to be paid by Zelle to this contact.</label>
                <Button onClick={() => run(async () => { await m5Api.electZelle({ verification_id: vid, signature_typed_name: name, consent: agree, consent_text_version: data.consent_text_version }); toast.success("Zelle election saved"); reset(); })} disabled={!agree || !name.trim()} data-testid="my-method-elect-zelle">Elect Zelle</Button>
              </div>
            )}
            <ErrorText message={err} testId="my-method-error" />
          </section>
          <section data-testid="my-method-history">
            <p className="label-caps mb-2 text-zinc-500">History</p>
            <ul className="space-y-1 text-xs text-zinc-400">{data.history.map((h) => <li key={h.id}>v{h.version} · {h.method}{h.recipient_masked ? ` → ${h.recipient_masked}` : ""} · {h.status} · {h.created_at && new Date(h.created_at).toLocaleDateString()}</li>)}</ul>
          </section>
        </>
      )}
    </div>
  );
}
